// "It wasn't me" — where the button in the "Was this you?" email lands (DESIGN-security.md
// step 2, Rob's 5A). NOT signed in: the link's one-time token is the only thing it holds.
//
// ⚠️ OPENING THIS PAGE CHANGES NOTHING. Outlook and Gmail open links in emails by themselves to
// scan them, so the page only ASKS (`notMeCheck`), and nobody is signed out until the button is
// pressed (`notMeConfirm`). Then: signed out everywhere, admins told, and — for somebody with a
// password — straight on to choosing a new one with a one-hour link the server hands back.
//
// ⚠️ Not wrapped in PublicRoute: whoever opens it may well be signed in in this browser, and
// being bounced to the dashboard would hide the one button that matters.
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, ShieldAlert, XCircle } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authApi, securityApi } from '../lib/api';
import { MIN_PASSWORD_LENGTH, PASSWORD_HINT, passwordTooShort } from '../lib/passwordRule';

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-secondary/20 p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8"><Logo size={34} /></div>
        <div className="bg-card border border-border rounded-xl p-8 shadow-lg text-center" data-testid="not-me-page">
          {children}
        </div>
      </div>
    </div>
  );
}

function Problem({ title, children }) {
  return (
    <Shell>
      <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
      <h1 className="text-xl font-heading font-bold mb-2">{title}</h1>
      <p className="text-sm text-muted-foreground mb-5">{children}</p>
      <Link to="/login"><Button className="btn-primary">Go to sign in</Button></Link>
    </Shell>
  );
}

export default function NotMePage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [info, setInfo] = useState(null);
  const [bad, setBad] = useState(null);           // null | 'invalid'
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);         // the confirm's answer
  const [error, setError] = useState(null);
  const [pw, setPw] = useState({ a: '', b: '' });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!token) { setBad('invalid'); return; }
    securityApi.notMeCheck(token)
      .then((r) => setInfo(r.data))
      .catch(() => setBad('invalid'));
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await securityApi.notMeConfirm(token);
      setDone(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'That didn’t work. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) { setError('The two passwords don’t match.'); return; }
    const short = passwordTooShort(pw.a);
    if (short) { setError(short); return; }
    setBusy(true);
    setError(null);
    try {
      await authApi.resetPassword(done.reset_token, pw.a);
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Couldn’t save that password. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (bad) {
    return <Problem title="This link isn’t valid">It may have been copied only partly. If you’re worried about your account, use “Forgot password” on the sign-in page.</Problem>;
  }
  if (!info) {
    return <Shell><Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" /></Shell>;
  }
  if (!done && info.state === 'used') {
    return <Problem title="This link has already been used">You’ve already been signed out everywhere with it. To choose a new password, use “Forgot password” on the sign-in page.</Problem>;
  }
  if (!done && info.state === 'expired') {
    return <Problem title="This link has run out">These links last 7 days. If you’re worried about your account, use “Forgot password” on the sign-in page — it signs you out everywhere too.</Problem>;
  }

  // ── Step 2: signed out — now a new password ────────────────────────────────────────────
  if (done) {
    if (saved) {
      return (
        <Shell>
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h1 className="text-xl font-heading font-bold mb-2">Your new password is saved</h1>
          <p className="text-sm text-muted-foreground mb-5">Sign in with it now. Your admins have been told what happened.</p>
          <Link to="/login"><Button className="btn-primary">Sign in</Button></Link>
        </Shell>
      );
    }
    return (
      <Shell>
        <span className="inline-block rounded-full px-2.5 py-0.5 text-xs font-bold bg-green-500/15 text-green-700 dark:text-green-400 mb-3">
          ✓ Signed out everywhere
        </span>
        {done.reset_token ? (
          <>
            <h1 className="text-xl font-heading font-bold mb-1">Now choose a new password</h1>
            <p className="text-sm text-muted-foreground mb-4">Whoever signed in knew your password, so they could sign in again.</p>
            <form onSubmit={savePassword} className="space-y-3 text-left">
              <div className="space-y-1.5">
                <Label htmlFor="pw-a">New password</Label>
                <Input id="pw-a" type="password" placeholder={PASSWORD_HINT} minLength={MIN_PASSWORD_LENGTH}
                       value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} className="input-hive" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw-b">Type it again</Label>
                <Input id="pw-b" type="password" value={pw.b}
                       onChange={(e) => setPw({ ...pw, b: e.target.value })} className="input-hive" required />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <Button type="submit" className="btn-primary w-full" disabled={busy} data-testid="not-me-save-password">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save my new password
              </Button>
            </form>
            <p className="text-xs text-muted-foreground mt-4">We’ve told your company’s admins, so they can keep an eye out.</p>
          </>
        ) : (
          <>
            <h1 className="text-xl font-heading font-bold mb-2">You’re signed out everywhere</h1>
            <p className="text-sm text-muted-foreground mb-5">
              {done.has_password
                ? 'Use “Forgot password” on the sign-in page to choose a new password.'
                : 'You sign in with Google or Microsoft, so there’s no HolidayHive password to change. Whoever got in may have that account too — change its password, and tell whoever looks after your IT.'}
              {' '}We’ve told your company’s admins.
            </p>
            <Link to="/login"><Button className="btn-primary">Go to sign in</Button></Link>
          </>
        )}
      </Shell>
    );
  }

  // ── Step 1: ask first ──────────────────────────────────────────────────────────────────
  const where = [info.device, info.country].filter(Boolean).join(' in ');
  return (
    <Shell>
      <ShieldAlert className="w-12 h-12 text-amber-500 mx-auto mb-3" />
      <h1 className="text-xl font-heading font-bold mb-2">Sign {info.first_name} out everywhere?</h1>
      <p className="text-sm text-muted-foreground mb-5">
        Every device signed in as <strong>{info.email}</strong> will be signed out on its next
        click{where ? <>, including <strong>{where}</strong></> : ''}{info.when ? ` (${info.when})` : ''}.
      </p>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      <Button className="btn-primary" disabled={busy} onClick={confirm} data-testid="not-me-confirm">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Yes, sign out everywhere
      </Button>
      <p className="text-xs text-muted-foreground mt-4">If that sign-in was you after all, just close this page — nothing has changed.</p>
    </Shell>
  );
}
